import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { restaurantsApi, kpiApi } from '../lib/api';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { RestaurantSelector } from '../components/dashboard/RestaurantSelector';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
    TrendingUp, 
    TrendingDown, 
    Utensils, 
    Wine,
    BarChart3,
    Calendar,
    Package
} from 'lucide-react';

// Mois en français
const MONTHS_FR = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
];

// Ratio Card Component
const RatioCard = ({ 
    title, 
    icon: Icon, 
    ratio, 
    ratioN1, 
    ratioBudget,
    details, 
    color = "blue",
    isYtd = false 
}) => {
    // Traiter undefined comme null
    const ratioValue = ratio ?? null;
    const ratioN1Value = ratioN1 ?? null;
    const ratioBudgetValue = ratioBudget ?? null;
    
    const ecartN1 = ratioValue !== null && ratioN1Value !== null ? ratioValue - ratioN1Value : null;
    const ecartBudget = ratioValue !== null && ratioBudgetValue !== null ? ratioValue - ratioBudgetValue : null;
    const isGoodN1 = ecartN1 !== null && ecartN1 < 0; // Un ratio plus bas est mieux
    const isGoodBudget = ecartBudget !== null && ecartBudget < 0; // Réalisé < Budget = bon
    
    const colorClasses = {
        blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30",
        amber: "from-amber-500/20 to-amber-600/10 border-amber-500/30",
        green: "from-green-500/20 to-green-600/10 border-green-500/30",
        purple: "from-purple-500/20 to-purple-600/10 border-purple-500/30"
    };
    
    const iconColors = {
        blue: "text-blue-500",
        amber: "text-amber-500",
        green: "text-green-500",
        purple: "text-purple-500"
    };
    
    return (
        <div className={cn(
            "rounded-2xl border bg-gradient-to-br p-6 transition-all hover:shadow-lg",
            colorClasses[color]
        )}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={cn("p-2 rounded-lg bg-white/50", iconColors[color])}>
                        <Icon className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg">{title}</h3>
                        <span className="text-xs text-muted-foreground">
                            {isYtd ? "Cumul YTD" : "Mois en cours"}
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Ratio principal */}
            <div className="text-center py-4">
                {ratioValue !== null ? (
                    <div className="text-5xl font-bold text-foreground">
                        {ratioValue.toFixed(1)}%
                    </div>
                ) : (
                    <div className="text-3xl text-muted-foreground">-</div>
                )}
            </div>
            
            {/* Comparaisons vs Budget et vs N-1 */}
            <div className="flex justify-center gap-6 mb-4">
                {/* vs Budget */}
                {ratioBudgetValue !== null && (
                    <div className="text-center">
                        <span className="text-xs text-muted-foreground block">vs Budget</span>
                        <span className="text-sm font-medium text-muted-foreground">
                            {ratioBudgetValue.toFixed(1)}%
                        </span>
                        {ecartBudget !== null && (
                            <span className={cn(
                                "flex items-center justify-center gap-0.5 text-xs font-semibold",
                                isGoodBudget ? "text-green-600" : "text-red-600"
                            )}>
                                {isGoodBudget ? (
                                    <TrendingDown className="w-3 h-3" />
                                ) : (
                                    <TrendingUp className="w-3 h-3" />
                                )}
                                {ecartBudget >= 0 ? '+' : ''}{ecartBudget.toFixed(1)}
                            </span>
                        )}
                    </div>
                )}
                
                {/* vs N-1 */}
                {ratioN1Value !== null && (
                    <div className="text-center">
                        <span className="text-xs text-muted-foreground block">vs N-1</span>
                        <span className="text-sm font-medium text-muted-foreground">
                            {ratioN1Value.toFixed(1)}%
                        </span>
                        {ecartN1 !== null && (
                            <span className={cn(
                                "flex items-center justify-center gap-0.5 text-xs font-semibold",
                                isGoodN1 ? "text-green-600" : "text-red-600"
                            )}>
                                {isGoodN1 ? (
                                    <TrendingDown className="w-3 h-3" />
                                ) : (
                                    <TrendingUp className="w-3 h-3" />
                                )}
                                {ecartN1 >= 0 ? '+' : ''}{ecartN1.toFixed(1)}
                            </span>
                        )}
                    </div>
                )}
            </div>
            
            {/* Détails */}
            {details && (
                <div className="border-t pt-4 mt-2 space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Coût matières</span>
                        <span className="font-medium">
                            {details.total_cout?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">CA</span>
                        <span className="font-medium">
                            {(details.ca_total || details.ca_boissons || details.ca_nourriture)?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function KPIPage() {
    const { user } = useAuth();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const currentMonth = new Date().getMonth() + 1;
        return currentMonth > 1 ? currentMonth - 1 : 1;
    });
    const [selectedYear] = useState(2026);
    const [kpiData, setKpiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [isYtd, setIsYtd] = useState(false);
    const [isConsolidated, setIsConsolidated] = useState(false);
    
    const currentMonth = new Date().getMonth() + 1;
    const availableMonths = MONTHS_FR.filter(m => m.value <= currentMonth);
    
    // Charger la liste des restaurants
    useEffect(() => {
        const loadRestaurants = async () => {
            try {
                const response = await restaurantsApi.getAll();
                const restos = response.data.filter(r => r.id !== 'all');
                setRestaurants(restos);
            } catch (error) {
                console.error('Erreur chargement restaurants:', error);
                toast.error('Erreur lors du chargement des restaurants');
            } finally {
                setLoading(false);
            }
        };
        
        loadRestaurants();
    }, [user]);
    
    // Auto-sélectionner le premier restaurant
    useEffect(() => {
        if (restaurants.length > 0 && !selectedRestaurant && !isConsolidated) {
            setSelectedRestaurant(restaurants[0]);
        }
    }, [restaurants, selectedRestaurant, isConsolidated]);
    
    // Charger les données KPI
    const loadKpiData = useCallback(async () => {
        if (!selectedRestaurant && !isConsolidated) return;
        
        setLoadingData(true);
        try {
            let response;
            if (isConsolidated) {
                response = await kpiApi.getRatiosConsolidated(selectedMonth, selectedYear);
            } else {
                response = await kpiApi.getRatios(selectedRestaurant.name, selectedMonth, selectedYear);
            }
            setKpiData(response.data);
        } catch (error) {
            console.error('Erreur chargement KPI:', error);
            if (error.response?.status === 404) {
                toast.error('Données comptables non disponibles');
            } else {
                toast.error('Erreur lors du chargement des KPI');
            }
            setKpiData(null);
        } finally {
            setLoadingData(false);
        }
    }, [selectedRestaurant, selectedMonth, selectedYear, isConsolidated]);
    
    useEffect(() => {
        loadKpiData();
    }, [loadKpiData]);
    
    // Extraire les données selon le mode (mois ou YTD)
    const currentData = isYtd ? kpiData?.ytd_data : kpiData?.month_data;
    const currentDataN1 = isYtd ? kpiData?.ytd_data_n1 : kpiData?.month_data_n1;
    
    // Extraire les données budget (seulement pour les restaurants individuels)
    const budgetData = !isConsolidated ? kpiData?.budget_data : null;
    
    const selectedMonthLabel = MONTHS_FR.find(m => m.value === selectedMonth)?.label || '';
    
    return (
        <div className="flex h-screen bg-background" data-testid="kpi-page">
            <Sidebar onCollapse={setSidebarCollapsed} />
            
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header title="KPI - Ratios" />
                
                <main className="flex-1 overflow-auto p-6">
                    {/* Sélecteur de restaurant */}
                    <div className="bg-card border border-border rounded-xl p-4 mb-6">
                        {loading ? (
                            <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                                {[...Array(8)].map((_, i) => (
                                    <Skeleton key={i} className="h-20 md:h-24 rounded-xl" />
                                ))}
                            </div>
                        ) : (
                            <RestaurantSelector
                                restaurants={restaurants}
                                selectedId={isConsolidated ? null : selectedRestaurant?.id}
                                onSelect={(restaurant) => {
                                    setIsConsolidated(false);
                                    setSelectedRestaurant(restaurant);
                                }}
                                showConsolidated={user?.role === 'admin'}
                                isConsolidated={isConsolidated}
                                onConsolidatedSelect={() => {
                                    setIsConsolidated(true);
                                    setSelectedRestaurant(null);
                                }}
                            />
                        )}
                    </div>
                    
                    {/* Titre et contrôles */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <BarChart3 className="w-6 h-6 text-primary" />
                            <h2 className="text-2xl font-bold">
                                {isConsolidated ? "Consolidation (Tous restaurants)" : selectedRestaurant?.name || "Sélectionnez un restaurant"}
                            </h2>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            {/* Sélecteur de mois */}
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                    className="px-3 py-1.5 rounded-lg border bg-background text-sm"
                                    data-testid="month-selector"
                                >
                                    {availableMonths.map(m => (
                                        <option key={m.value} value={m.value}>
                                            {m.label} {selectedYear}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Toggle Mois/YTD */}
                            <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                                <button
                                    onClick={() => setIsYtd(false)}
                                    className={cn(
                                        "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                                        !isYtd 
                                            ? "bg-white shadow text-foreground" 
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                    data-testid="mode-month"
                                >
                                    Mois
                                </button>
                                <button
                                    onClick={() => setIsYtd(true)}
                                    className={cn(
                                        "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                                        isYtd 
                                            ? "bg-white shadow text-foreground" 
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                    data-testid="mode-ytd"
                                >
                                    YTD
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Cartes KPI */}
                    {loadingData ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Skeleton className="h-64 rounded-2xl" />
                            <Skeleton className="h-64 rounded-2xl" />
                            <Skeleton className="h-64 rounded-2xl" />
                        </div>
                    ) : kpiData?.error ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>{kpiData.error}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Ratio Nourriture */}
                            <RatioCard
                                title="Ratio Nourriture"
                                icon={Utensils}
                                ratio={currentData?.ratio_nourriture}
                                ratioN1={currentDataN1?.ratio_nourriture}
                                ratioBudget={budgetData?.ratio_nourriture_budget}
                                details={currentData?.details_nourriture}
                                color="amber"
                                isYtd={isYtd}
                            />
                            
                            {/* Ratio Boissons */}
                            <RatioCard
                                title="Ratio Boissons"
                                icon={Wine}
                                ratio={currentData?.ratio_boissons}
                                ratioN1={currentDataN1?.ratio_boissons}
                                ratioBudget={budgetData?.ratio_boissons_budget}
                                details={currentData?.details_boissons}
                                color="purple"
                                isYtd={isYtd}
                            />
                            
                            {/* Ratio Coût Matière (combiné) */}
                            <RatioCard
                                title="Ratio Coût Matière"
                                icon={Package}
                                ratio={currentData?.ratio_cout_matiere}
                                ratioN1={currentDataN1?.ratio_cout_matiere}
                                ratioBudget={budgetData?.ratio_cout_matiere_budget}
                                details={currentData?.details_cout_matiere}
                                color="blue"
                                isYtd={isYtd}
                            />
                        </div>
                    )}
                    
                    {/* Tableau détaillé par restaurant (mode consolidé) */}
                    {isConsolidated && kpiData?.by_restaurant && (
                        <div className="mt-8">
                            <h3 className="text-lg font-semibold mb-4">Détail par restaurant</h3>
                            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-slate-100 text-left">
                                            <th className="px-4 py-3 font-semibold text-sm">Restaurant</th>
                                            <th className="px-4 py-3 font-semibold text-sm text-right">Coût Matière</th>
                                            <th className="px-4 py-3 font-semibold text-sm text-right">vs N-1</th>
                                            <th className="px-4 py-3 font-semibold text-sm text-right">Nourriture</th>
                                            <th className="px-4 py-3 font-semibold text-sm text-right">vs N-1</th>
                                            <th className="px-4 py-3 font-semibold text-sm text-right">Boissons</th>
                                            <th className="px-4 py-3 font-semibold text-sm text-right">vs N-1</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(kpiData.by_restaurant).map(([name, data]) => {
                                            const md = isYtd ? data.ytd_data : data.month_data;
                                            const mdN1 = isYtd ? data.ytd_data_n1 : data.month_data_n1;
                                            const ecartCM = md?.ratio_cout_matiere != null && mdN1?.ratio_cout_matiere != null 
                                                ? md.ratio_cout_matiere - mdN1.ratio_cout_matiere : null;
                                            const ecartNour = md?.ratio_nourriture != null && mdN1?.ratio_nourriture != null 
                                                ? md.ratio_nourriture - mdN1.ratio_nourriture : null;
                                            const ecartBoiss = md?.ratio_boissons != null && mdN1?.ratio_boissons != null 
                                                ? md.ratio_boissons - mdN1.ratio_boissons : null;
                                            
                                            return (
                                                <tr key={name} className="border-t hover:bg-slate-50">
                                                    <td className="px-4 py-3 font-medium">{name}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums font-semibold">
                                                        {md?.ratio_cout_matiere != null ? `${md.ratio_cout_matiere.toFixed(1)}%` : '-'}
                                                    </td>
                                                    <td className={cn(
                                                        "px-4 py-3 text-right tabular-nums text-sm",
                                                        ecartCM !== null && (ecartCM < 0 ? "text-green-600" : "text-red-600")
                                                    )}>
                                                        {ecartCM !== null ? `${ecartCM >= 0 ? '+' : ''}${ecartCM.toFixed(1)}` : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {md?.ratio_nourriture != null ? `${md.ratio_nourriture.toFixed(1)}%` : '-'}
                                                    </td>
                                                    <td className={cn(
                                                        "px-4 py-3 text-right tabular-nums text-sm",
                                                        ecartNour !== null && (ecartNour < 0 ? "text-green-600" : "text-red-600")
                                                    )}>
                                                        {ecartNour !== null ? `${ecartNour >= 0 ? '+' : ''}${ecartNour.toFixed(1)}` : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {md?.ratio_boissons != null ? `${md.ratio_boissons.toFixed(1)}%` : '-'}
                                                    </td>
                                                    <td className={cn(
                                                        "px-4 py-3 text-right tabular-nums text-sm",
                                                        ecartBoiss !== null && (ecartBoiss < 0 ? "text-green-600" : "text-red-600")
                                                    )}>
                                                        {ecartBoiss !== null ? `${ecartBoiss >= 0 ? '+' : ''}${ecartBoiss.toFixed(1)}` : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
